/**
 * Catalog of ready-made MCP servers ("presets"). Every entry is a vendor-hosted remote server whose
 * endpoint and auth were verified against the live server (OAuth protected-resource metadata, and
 * dynamic client registration where `oauth.registration` is `dynamic`) on 2026-10-03.
 *
 * Adding a preset creates a normal `mcp_servers` row (transport/url/authType from the preset), so
 * presets and custom servers share discovery, policies, bindings and auditing.
 */

export const MCP_PRESET_CATEGORIES = [
  'planning',
  'docs',
  'google',
  'files',
  'design',
  'meetings',
  'dev',
  'research',
  'sales',
  'payments',
  'automation',
] as const;
export type McpPresetCategory = (typeof MCP_PRESET_CATEGORIES)[number];

export const MCP_PRESET_CATEGORY_LABELS: Record<McpPresetCategory, string> = {
  planning: 'Planning & tasks',
  docs: 'Docs & knowledge',
  google: 'Google',
  files: 'Files',
  design: 'Design & whiteboards',
  meetings: 'Meetings',
  dev: 'Development',
  research: 'Research & docs lookup',
  sales: 'CRM & support',
  payments: 'Payments',
  automation: 'Automation',
};

export type McpPresetAuth =
  /** No credentials needed. */
  | { kind: 'none' }
  /**
   * MCP OAuth. `dynamic`: the authorization server supports dynamic client registration, so the
   * user only signs in. `manual`: register an OAuth app at the vendor first and paste its client
   * id/secret (the redirect URI to register is shown in the UI).
   */
  | {
      kind: 'oauth';
      registration: 'dynamic' | 'manual';
      /** Scopes to request (default: what the server advertises; `[]`: send no scope). */
      scopes?: string[];
      /** Extra authorize-URL parameters (e.g. Google's offline access). */
      authParams?: Record<string, string>;
      /** Send the RFC 8707 `resource` parameter (default true). */
      resourceParam?: boolean;
      /** Where to create the OAuth app (manual registration). */
      setupUrl?: string;
      /** What to create there, e.g. "a GitHub App" (default "an OAuth app"). */
      setupLabel?: string;
    }
  /** A personal API key / token, sent as `Authorization: Bearer` or a custom header. */
  | { kind: 'token'; authType: 'bearer' | `header:${string}`; tokenUrl: string; tokenLabel: string };

export interface McpPreset {
  id: string;
  name: string;
  vendor: string;
  category: McpPresetCategory;
  description: string;
  transport: 'http' | 'sse';
  url: string;
  auth: McpPresetAuth;
  /** A second way to connect (offered next to `auth` when adding; `auth` is the default). */
  altAuth?: McpPresetAuth;
  /** Suggested tool prefix when binding to an agent (`mcp.<prefix>.<tool>`). */
  prefix: string;
  docsUrl: string;
  /** Monogram tile colour (brand-ish). */
  color: string;
  /** Short caveat shown on the card (preview programs, plan requirements, money movement…). */
  note?: string;
  /**
   * Self-hosted products: the endpoint differs per installation, so the user enters it (`url` is
   * then only the placeholder). `prefill`: a hosted default exists, so start the field at `url`.
   */
  urlInput?: { label: string; hint: string; prefill?: boolean };
  /**
   * Web apps this server automates (`host`, `*.host` or `host/path-prefix`), used by recording
   * mode to map a recorded page to an integration. Self-hosted presets (`urlInput`) match on the
   * host of the configured server URL instead.
   */
  hosts?: readonly string[];
  /**
   * The repositories the stored credential reaches can be listed (`listMcpRepos`): `github` via the
   * REST API, `gitlab` via the MCP server's `list_projects` tool.
   */
  repoApi?: 'github' | 'gitlab';
}

const dyn = { kind: 'oauth', registration: 'dynamic' } as const;

const GOOGLE_SETUP = 'https://console.cloud.google.com/apis/credentials';
const google = (scopes: string[]): McpPresetAuth => ({
  kind: 'oauth',
  registration: 'manual',
  scopes,
  // Refresh tokens are only issued for offline access; `consent` makes Google return one again on reconnect.
  authParams: { access_type: 'offline', prompt: 'consent' },
  resourceParam: false,
  setupUrl: GOOGLE_SETUP,
});
const GOOGLE_NOTE =
  'Google Workspace MCP developer preview: create a "Web application" OAuth client in your own Google Cloud project, enable the product API and its MCP API, and register the redirect URI below. In "Testing" mode Google expires refresh tokens after 7 days.';

export const MCP_PRESETS: readonly McpPreset[] = [
  // ---------- Planning & tasks ----------
  {
    id: 'trello',
    name: 'Trello',
    vendor: 'Atlassian',
    category: 'planning',
    description: 'Boards, lists and cards: plan work, move cards, add checklists and comments.',
    transport: 'http',
    url: 'https://mcp.trello.com/v1',
    auth: dyn,
    prefix: 'trello',
    hosts: ['trello.com'],
    docsUrl: 'https://github.com/atlassian/trello-mcp-server',
    color: '#0c66e4',
  },
  {
    id: 'linear',
    name: 'Linear',
    vendor: 'Linear',
    category: 'planning',
    description: 'Issues, projects, cycles and comments.',
    transport: 'http',
    url: 'https://mcp.linear.app/mcp',
    auth: dyn,
    prefix: 'linear',
    hosts: ['linear.app'],
    docsUrl: 'https://linear.app/docs/mcp',
    color: '#5e6ad2',
  },
  {
    id: 'atlassian',
    name: 'Jira & Confluence',
    vendor: 'Atlassian',
    category: 'planning',
    description: 'Search and update Jira issues and Confluence pages.',
    transport: 'http',
    url: 'https://mcp.atlassian.com/v1/mcp',
    auth: dyn,
    prefix: 'atlassian',
    hosts: ['*.atlassian.net'],
    docsUrl: 'https://www.atlassian.com/platform/remote-mcp-server',
    color: '#1868db',
  },
  {
    id: 'todoist',
    name: 'Todoist',
    vendor: 'Doist',
    category: 'planning',
    description: 'Tasks, projects, due dates and labels.',
    transport: 'http',
    url: 'https://ai.todoist.net/mcp',
    auth: dyn,
    prefix: 'todoist',
    hosts: ['app.todoist.com', 'todoist.com'],
    docsUrl: 'https://www.todoist.com/help/articles/use-todoist-with-ai-assistants',
    color: '#e44332',
  },
  {
    id: 'clickup',
    name: 'ClickUp',
    vendor: 'ClickUp',
    category: 'planning',
    description: 'Tasks, docs and chat in your ClickUp workspace.',
    transport: 'http',
    url: 'https://mcp.clickup.com/mcp',
    auth: dyn,
    prefix: 'clickup',
    hosts: ['app.clickup.com'],
    docsUrl: 'https://developer.clickup.com/docs/connect-an-ai-assistant-to-clickups-mcp-server',
    color: '#7b68ee',
  },
  {
    id: 'monday',
    name: 'monday.com',
    vendor: 'monday.com',
    category: 'planning',
    description: 'Boards, items and updates.',
    transport: 'http',
    url: 'https://mcp.monday.com/mcp',
    auth: dyn,
    prefix: 'monday',
    hosts: ['*.monday.com'],
    docsUrl: 'https://developer.monday.com/apps/docs/mondaycom-mcp-integration',
    color: '#ff3d57',
  },
  {
    id: 'airtable',
    name: 'Airtable',
    vendor: 'Airtable',
    category: 'planning',
    description: 'Read and write records, inspect base schemas.',
    transport: 'http',
    url: 'https://mcp.airtable.com/mcp',
    auth: dyn,
    prefix: 'airtable',
    hosts: ['airtable.com'],
    docsUrl: 'https://support.airtable.com/docs/using-the-airtable-mcp-server',
    color: '#18bfff',
  },

  // ---------- Docs & knowledge ----------
  {
    id: 'notion',
    name: 'Notion',
    vendor: 'Notion',
    category: 'docs',
    description: 'Search, read and write pages and databases.',
    transport: 'http',
    url: 'https://mcp.notion.com/mcp',
    auth: dyn,
    prefix: 'notion',
    hosts: ['notion.so', 'www.notion.so', '*.notion.site'],
    docsUrl: 'https://developers.notion.com/docs/mcp',
    color: '#191919',
  },

  // ---------- Google ----------
  {
    id: 'gmail',
    name: 'Gmail',
    vendor: 'Google',
    category: 'google',
    description: 'Search and read mail, write drafts.',
    transport: 'http',
    url: 'https://gmailmcp.googleapis.com/mcp/v1',
    auth: google([
      'https://www.googleapis.com/auth/gmail.readonly',
      'https://www.googleapis.com/auth/gmail.compose',
    ]),
    prefix: 'gmail',
    hosts: ['mail.google.com'],
    docsUrl: 'https://developers.google.com/workspace/guides/configure-mcp-servers',
    color: '#ea4335',
    note: GOOGLE_NOTE,
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    vendor: 'Google',
    category: 'google',
    description: 'Find free time, read and manage events.',
    transport: 'http',
    url: 'https://calendarmcp.googleapis.com/mcp/v1',
    auth: google([
      'https://www.googleapis.com/auth/calendar.calendarlist.readonly',
      'https://www.googleapis.com/auth/calendar.events.freebusy',
      'https://www.googleapis.com/auth/calendar.events',
    ]),
    prefix: 'gcal',
    hosts: ['calendar.google.com'],
    docsUrl: 'https://developers.google.com/workspace/guides/configure-mcp-servers',
    color: '#1a73e8',
    note: GOOGLE_NOTE,
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    vendor: 'Google',
    category: 'google',
    description: 'Search, read and upload files.',
    transport: 'http',
    url: 'https://drivemcp.googleapis.com/mcp/v1',
    auth: google([
      'https://www.googleapis.com/auth/drive.readonly',
      'https://www.googleapis.com/auth/drive.file',
    ]),
    prefix: 'gdrive',
    hosts: ['drive.google.com'],
    docsUrl: 'https://developers.google.com/workspace/guides/configure-mcp-servers',
    color: '#1fa463',
    note: GOOGLE_NOTE,
  },
  {
    id: 'google-docs',
    name: 'Google Docs',
    vendor: 'Google',
    category: 'google',
    description: 'Read and edit documents.',
    transport: 'http',
    url: 'https://docsmcp.googleapis.com/mcp/v1',
    auth: google([
      'https://www.googleapis.com/auth/drive.readonly',
      'https://www.googleapis.com/auth/documents',
    ]),
    prefix: 'gdocs',
    hosts: ['docs.google.com/document'],
    docsUrl: 'https://developers.google.com/workspace/guides/configure-mcp-servers',
    color: '#4285f4',
    note: GOOGLE_NOTE,
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    vendor: 'Google',
    category: 'google',
    description: 'Read and update spreadsheets.',
    transport: 'http',
    url: 'https://sheetsmcp.googleapis.com/mcp/v1',
    auth: google([
      'https://www.googleapis.com/auth/drive.readonly',
      'https://www.googleapis.com/auth/spreadsheets',
    ]),
    prefix: 'gsheets',
    hosts: ['docs.google.com/spreadsheets'],
    docsUrl: 'https://developers.google.com/workspace/guides/configure-mcp-servers',
    color: '#0f9d58',
    note: GOOGLE_NOTE,
  },

  // ---------- Files ----------
  {
    id: 'dropbox',
    name: 'Dropbox',
    vendor: 'Dropbox',
    category: 'files',
    description: 'Search, read and organize files.',
    transport: 'http',
    url: 'https://mcp.dropbox.com/mcp',
    auth: dyn,
    prefix: 'dropbox',
    hosts: ['www.dropbox.com'],
    docsUrl: 'https://help.dropbox.com/integrations/connect-dropbox-mcp-server',
    color: '#0061fe',
  },
  {
    id: 'box',
    name: 'Box',
    vendor: 'Box',
    category: 'files',
    description: 'Search files, read content and ask questions about documents.',
    transport: 'http',
    url: 'https://mcp.box.com',
    auth: { kind: 'oauth', registration: 'manual', setupUrl: 'https://app.box.com/developers/console' },
    prefix: 'box',
    hosts: ['app.box.com', '*.app.box.com'],
    docsUrl: 'https://developer.box.com/guides/box-mcp/remote/',
    color: '#0061d5',
    note: 'Create a Box Platform app with OAuth 2.0 and register the redirect URI below.',
  },

  // ---------- Design & whiteboards ----------
  {
    id: 'miro',
    name: 'Miro',
    vendor: 'Miro',
    category: 'design',
    description: 'Read boards and create diagrams, stickies and docs on them.',
    transport: 'http',
    url: 'https://mcp.miro.com/',
    auth: dyn,
    prefix: 'miro',
    hosts: ['miro.com'],
    docsUrl: 'https://developers.miro.com/docs/miro-mcp',
    color: '#ffd02f',
  },
  {
    id: 'canva',
    name: 'Canva',
    vendor: 'Canva',
    category: 'design',
    description: 'Search, create and export designs.',
    transport: 'http',
    url: 'https://mcp.canva.com/mcp',
    auth: dyn,
    prefix: 'canva',
    hosts: ['www.canva.com'],
    docsUrl: 'https://www.canva.dev/docs/connect/canva-mcp-server-setup/',
    color: '#00c4cc',
  },
  {
    id: 'figma',
    name: 'Figma',
    vendor: 'Figma',
    category: 'design',
    description: 'Read designs, components and variables.',
    transport: 'http',
    url: 'https://mcp.figma.com/mcp',
    auth: dyn,
    prefix: 'figma',
    hosts: ['www.figma.com'],
    docsUrl: 'https://developers.figma.com/docs/figma-mcp-server/remote-server-installation/',
    color: '#a259ff',
    note: 'Figma may only accept MCP clients it has approved; sign-in can be refused.',
  },
  {
    id: 'gamma',
    name: 'Gamma',
    vendor: 'Gamma',
    category: 'design',
    description: 'Generate presentations, documents and web pages.',
    transport: 'http',
    url: 'https://mcp.gamma.app/mcp',
    auth: dyn,
    prefix: 'gamma',
    hosts: ['gamma.app'],
    docsUrl: 'https://developers.gamma.app/docs/mcp',
    color: '#7c3aed',
  },

  // ---------- Meetings ----------
  {
    id: 'granola',
    name: 'Granola',
    vendor: 'Granola',
    category: 'meetings',
    description: 'Meeting notes and transcripts.',
    transport: 'http',
    url: 'https://mcp.granola.ai/mcp',
    auth: dyn,
    prefix: 'granola',
    hosts: ['app.granola.ai'],
    docsUrl: 'https://docs.granola.ai/help-center/sharing/integrations/mcp',
    color: '#4c8f3a',
  },
  {
    id: 'fireflies',
    name: 'Fireflies',
    vendor: 'Fireflies.ai',
    category: 'meetings',
    description: 'Search meeting transcripts and summaries.',
    transport: 'http',
    url: 'https://mcp.fireflies.ai/mcp',
    auth: dyn,
    prefix: 'fireflies',
    hosts: ['app.fireflies.ai'],
    docsUrl: 'https://docs.fireflies.ai/getting-started/mcp-configuration',
    color: '#e5136f',
  },

  // ---------- Development ----------
  {
    id: 'github',
    name: 'GitHub',
    vendor: 'GitHub',
    category: 'dev',
    description: 'Repositories, issues, pull requests and Actions.',
    transport: 'http',
    url: 'https://api.githubcopilot.com/mcp/',
    auth: {
      kind: 'token',
      authType: 'bearer',
      tokenUrl: 'https://github.com/settings/personal-access-tokens/new',
      tokenLabel: 'Fine-grained personal access token',
    },
    // github.com/login/oauth has no dynamic registration, so sign-in needs a GitHub App registered
    // once (its user tokens expire and refresh; access = the app's permissions × the user's repos).
    // `scope` only matters if an OAuth App is pasted instead; GitHub ignores `resource`.
    altAuth: {
      kind: 'oauth',
      registration: 'manual',
      scopes: ['repo', 'read:org', 'read:user'],
      resourceParam: false,
      setupUrl: 'https://github.com/settings/apps/new',
      setupLabel: 'a GitHub App',
    },
    prefix: 'github',
    hosts: ['github.com'],
    repoApi: 'github',
    docsUrl: 'https://github.com/github/github-mcp-server',
    color: '#24292f',
    note: 'A fine-grained token limits access to the repos you pick. Sign-in needs a GitHub App (with "Expire user authorization tokens" on) registered once.',
  },
  {
    id: 'gitlab',
    name: 'GitLab',
    vendor: 'GitLab',
    category: 'dev',
    description: 'Projects, issues, merge requests, pipelines and code search.',
    transport: 'http',
    url: 'https://gitlab.com/api/v4/mcp',
    // gitlab.com advertises a registration endpoint and the single `mcp` scope.
    auth: dyn,
    prefix: 'gitlab',
    hosts: ['gitlab.com'],
    repoApi: 'gitlab',
    docsUrl: 'https://docs.gitlab.com/user/gitlab_duo/model_context_protocol/mcp_server/',
    color: '#fc6d26',
    note: 'Self-managed GitLab (18.6+): replace the URL with your instance + /api/v4/mcp and enable the MCP server there.',
    urlInput: {
      label: 'GitLab MCP URL',
      hint: 'gitlab.com is filled in; for self-managed use your address + /api/v4/mcp.',
      prefill: true,
    },
  },
  {
    id: 'sentry',
    name: 'Sentry',
    vendor: 'Sentry',
    category: 'dev',
    description: 'Issues, errors, traces and releases.',
    transport: 'http',
    url: 'https://mcp.sentry.dev/mcp',
    auth: dyn,
    prefix: 'sentry',
    hosts: ['*.sentry.io', 'sentry.io'],
    docsUrl: 'https://docs.sentry.io/product/sentry-mcp/',
    color: '#362d59',
  },
  {
    id: 'vercel',
    name: 'Vercel',
    vendor: 'Vercel',
    category: 'dev',
    description: 'Projects, deployments and logs.',
    transport: 'http',
    url: 'https://mcp.vercel.com',
    auth: dyn,
    prefix: 'vercel',
    hosts: ['vercel.com'],
    docsUrl: 'https://vercel.com/docs/mcp/vercel-mcp',
    color: '#000000',
  },
  {
    id: 'supabase',
    name: 'Supabase',
    vendor: 'Supabase',
    category: 'dev',
    description: 'Projects, tables, SQL, edge functions and logs.',
    transport: 'http',
    url: 'https://mcp.supabase.com/mcp',
    auth: dyn,
    prefix: 'supabase',
    hosts: ['supabase.com'],
    docsUrl: 'https://supabase.com/docs/guides/getting-started/mcp',
    color: '#3ecf8e',
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    vendor: 'Cloudflare',
    category: 'dev',
    description: 'Accounts, Workers, DNS and the rest of the Cloudflare API.',
    transport: 'http',
    url: 'https://mcp.cloudflare.com/mcp',
    auth: dyn,
    prefix: 'cloudflare',
    hosts: ['dash.cloudflare.com'],
    docsUrl: 'https://developers.cloudflare.com/agents/model-context-protocol/mcp-servers-for-cloudflare/',
    color: '#f38020',
  },
  {
    id: 'huggingface',
    name: 'Hugging Face',
    vendor: 'Hugging Face',
    category: 'dev',
    description: 'Search models, datasets, Spaces and papers.',
    transport: 'http',
    url: 'https://huggingface.co/mcp',
    auth: dyn,
    prefix: 'hf',
    hosts: ['huggingface.co'],
    docsUrl: 'https://huggingface.co/docs/hub/hf-mcp-server',
    color: '#ffb000',
  },

  // ---------- Research & docs lookup ----------
  {
    id: 'context7',
    name: 'Context7',
    vendor: 'Upstash',
    category: 'research',
    description: 'Up-to-date library and framework documentation for coding agents.',
    transport: 'http',
    url: 'https://mcp.context7.com/mcp',
    auth: { kind: 'none' },
    prefix: 'context7',
    hosts: ['context7.com'],
    docsUrl: 'https://context7.com/docs',
    color: '#059669',
    note: 'Works without an account (rate limited).',
  },
  {
    id: 'deepwiki',
    name: 'DeepWiki',
    vendor: 'Cognition',
    category: 'research',
    description: 'Ask questions about any public GitHub repository.',
    transport: 'http',
    url: 'https://mcp.deepwiki.com/mcp',
    auth: { kind: 'none' },
    prefix: 'deepwiki',
    hosts: ['deepwiki.com'],
    docsUrl: 'https://docs.devin.ai/work-with-devin/deepwiki-mcp',
    color: '#2563eb',
  },
  {
    id: 'exa',
    name: 'Exa',
    vendor: 'Exa',
    category: 'research',
    description: 'Web search and page contents built for agents.',
    transport: 'http',
    url: 'https://mcp.exa.ai/mcp',
    auth: { kind: 'none' },
    prefix: 'exa',
    hosts: ['exa.ai'],
    docsUrl: 'https://docs.exa.ai/reference/exa-mcp',
    color: '#1f40ed',
    note: 'Free tier without an account (rate limited).',
  },

  // ---------- CRM & support ----------
  {
    id: 'hubspot',
    name: 'HubSpot',
    vendor: 'HubSpot',
    category: 'sales',
    description: 'Contacts, companies, deals and tickets.',
    transport: 'http',
    url: 'https://mcp.hubspot.com/',
    auth: {
      kind: 'oauth',
      registration: 'manual',
      setupUrl:
        'https://developers.hubspot.com/docs/apps/developer-platform/build-apps/integrate-with-the-remote-hubspot-mcp-server',
    },
    prefix: 'hubspot',
    hosts: ['app.hubspot.com', 'app-eu1.hubspot.com'],
    docsUrl: 'https://developers.hubspot.com/mcp',
    color: '#ff7a59',
    note: 'Create an MCP auth app in your HubSpot developer account and register the redirect URI below.',
  },
  {
    id: 'attio',
    name: 'Attio',
    vendor: 'Attio',
    category: 'sales',
    description: 'People, companies, deals, notes and tasks.',
    transport: 'http',
    url: 'https://mcp.attio.com/mcp',
    auth: dyn,
    prefix: 'attio',
    hosts: ['app.attio.com'],
    docsUrl: 'https://docs.attio.com/mcp/overview',
    color: '#0f0f10',
  },
  {
    id: 'close',
    name: 'Close',
    vendor: 'Close',
    category: 'sales',
    description: 'Leads, opportunities, calls and emails.',
    transport: 'http',
    url: 'https://mcp.close.com/mcp',
    auth: dyn,
    prefix: 'close',
    hosts: ['app.close.com'],
    docsUrl: 'https://developer.close.com/mcp',
    color: '#2b6ff7',
  },
  {
    id: 'intercom',
    name: 'Intercom',
    vendor: 'Intercom',
    category: 'sales',
    description: 'Conversations, contacts and help-center articles.',
    transport: 'http',
    url: 'https://mcp.intercom.com/mcp',
    auth: dyn,
    prefix: 'intercom',
    hosts: ['app.intercom.com'],
    docsUrl: 'https://developers.intercom.com/docs/guides/mcp',
    color: '#1f8ded',
    note: 'US-hosted workspaces only.',
  },
  {
    id: 'slack',
    name: 'Slack',
    vendor: 'Slack',
    category: 'sales',
    description: 'Search messages, read channels and post replies.',
    transport: 'http',
    url: 'https://mcp.slack.com/mcp',
    auth: { kind: 'oauth', registration: 'manual', setupUrl: 'https://api.slack.com/apps' },
    prefix: 'slack',
    hosts: ['app.slack.com', '*.slack.com'],
    docsUrl: 'https://docs.slack.dev/ai/mcp-server',
    color: '#4a154b',
    note: 'Create a Slack app with MCP enabled and register the redirect URI below.',
  },

  // ---------- Payments ----------
  {
    id: 'stripe',
    name: 'Stripe',
    vendor: 'Stripe',
    category: 'payments',
    description: 'Customers, invoices, payment links and Stripe docs.',
    transport: 'http',
    url: 'https://mcp.stripe.com',
    auth: dyn,
    prefix: 'stripe',
    hosts: ['dashboard.stripe.com'],
    docsUrl: 'https://docs.stripe.com/mcp',
    color: '#635bff',
    note: 'Can move money. Keep an "ask" policy on its write tools.',
  },
  {
    id: 'paypal',
    name: 'PayPal',
    vendor: 'PayPal',
    category: 'payments',
    description: 'Invoices, orders, subscriptions and disputes.',
    transport: 'http',
    url: 'https://mcp.paypal.com/mcp',
    auth: dyn,
    prefix: 'paypal',
    hosts: ['www.paypal.com'],
    docsUrl: 'https://developer.paypal.com/tools/mcp-server/',
    color: '#003087',
    note: 'Can move money. Keep an "ask" policy on its write tools.',
  },

  // ---------- Automation ----------
  {
    id: 'zapier',
    name: 'Zapier',
    vendor: 'Zapier',
    category: 'automation',
    description: 'Run actions in 8,000+ apps through your Zapier account.',
    transport: 'http',
    url: 'https://mcp.zapier.com/api/mcp/mcp',
    auth: dyn,
    prefix: 'zapier',
    hosts: ['zapier.com'],
    docsUrl: 'https://zapier.com/mcp',
    color: '#ff4f00',
  },
  {
    id: 'n8n',
    name: 'n8n',
    vendor: 'n8n',
    category: 'automation',
    description: 'Search, run and build workflows on your own n8n instance.',
    transport: 'http',
    url: 'https://n8n.example.com/mcp-server/http',
    // n8n advertises only two legacy tool scopes; requesting them would limit the grant to those tools.
    auth: { kind: 'oauth', registration: 'dynamic', scopes: [] },
    prefix: 'n8n',
    docsUrl: 'https://docs.n8n.io/advanced-ai/accessing-n8n-mcp-server/',
    color: '#ea4b71',
    note: 'Self-hosted: turn on Settings → Instance-level MCP in n8n and enable MCP access per workflow.',
    urlInput: {
      label: 'n8n MCP URL',
      hint: 'Your n8n address + /mcp-server/http (shown in n8n under Settings → Instance-level MCP).',
    },
  },
];

export const MCP_PRESETS_BY_ID: ReadonlyMap<string, McpPreset> = new Map(MCP_PRESETS.map((p) => [p.id, p]));

/** Every way to connect a preset; the first is the default. */
export const presetAuths = (p: McpPreset): McpPresetAuth[] => (p.altAuth ? [p.auth, p.altAuth] : [p.auth]);

/** The preset's auth of this kind (`auth` or `altAuth`), if it offers one. */
export function presetAuthOf<K extends McpPresetAuth['kind']>(
  p: McpPreset | undefined,
  kind: K,
): Extract<McpPresetAuth, { kind: K }> | undefined {
  return p
    ? (presetAuths(p).find((a) => a.kind === kind) as Extract<McpPresetAuth, { kind: K }> | undefined)
    : undefined;
}

/** Path of the OAuth redirect URI on the app origin (register `<APP_ORIGIN><path>` at the vendor). */
export const MCP_OAUTH_CALLBACK_PATH = '/api/mcp-oauth/callback';

/** Does a page URL match a `hosts` pattern (`host`, `*.host` or `host/path-prefix`)? */
export function matchesHostPattern(pattern: string, pageUrl: string): boolean {
  const u = splitUrl(pageUrl);
  if (!u) return false;
  const slash = pattern.indexOf('/');
  const host = (slash === -1 ? pattern : pattern.slice(0, slash)).toLowerCase();
  const path = slash === -1 ? '' : pattern.slice(slash);
  const hostOk = host.startsWith('*.')
    ? u.host === host.slice(2) || u.host.endsWith(host.slice(1))
    : u.host === host;
  return hostOk && (!path || u.path.startsWith(path));
}

/** `https://Host:443/a/b?x` → `{ host: 'host', path: '/a/b' }` (no URL global in this package). */
export function splitUrl(pageUrl: string): { host: string; path: string } | null {
  const m = /^[a-z][a-z0-9+.-]*:\/\/(?:[^@/?#]*@)?([^/:?#]+)(?::\d+)?([^?#]*)/i.exec(pageUrl.trim());
  if (!m) return null;
  return { host: (m[1] as string).toLowerCase(), path: m[2] || '/' };
}

/** Catalog presets whose web app is at this URL (most specific pattern first). */
export function presetsForUrl(pageUrl: string): McpPreset[] {
  const hits: { p: McpPreset; len: number }[] = [];
  for (const p of MCP_PRESETS) {
    const best = Math.max(
      -1,
      ...(p.hosts ?? []).filter((h) => matchesHostPattern(h, pageUrl)).map((h) => h.length),
    );
    if (best >= 0) hits.push({ p, len: best });
  }
  return hits.sort((a, b) => b.len - a.len).map((x) => x.p);
}
